import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import Loader from 'react-loader-spinner';
import { closeDialog } from '../../../app/dialog/dialog.actions';
import { ConnectedDialog } from '../ConnectedDialog';
import { useApiRequestState } from '../../../utils/api.utils';
import { syncWrikeFolders } from '../../../app/page/page-storage/wrike/wrike.actions';
import { useSelect, useSelectWithParams } from '../../../utils/selector.utils';
import { wrikeFolderMeetingSelectors } from '../../../app/resource/wrike-folder-meeting/wrike-folder-meeting.selectors';
import { wrikeFolderSelectors } from '../../../app/resource/wrike-folder/wrike-folder.selectors';
import { SelectField } from './SelectField';
import { IWrikeFoldersDialogProps } from './types';
import { dialogSelectors } from '../../../app/dialog/dialog.selectors';

export const WrikeFoldersDialog: FunctionComponent = () => {
  const dispatch = useDispatch();
  const [showWrikeSyncLoader] = useApiRequestState('syncWrikeFolders');
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const props = useSelectWithParams(
    dialogSelectors.selectDialogProps,
    'wrikeFoldersDialog',
  ) as IWrikeFoldersDialogProps;

  const meetingId = props?.meetingId;

  const wrikeFolderMeetings = useSelect(wrikeFolderMeetingSelectors.selectResourceList);
  const wrikeRemoteFolders = useSelect(wrikeFolderSelectors.selectResourceList);

  const wrikeFolderMeetingAssociation = useMemo(() => {
    const wrikeFolderMeeting = wrikeFolderMeetings.find((meeting) => meeting.meetingId === String(meetingId));
    return wrikeFolderMeeting
      ? wrikeRemoteFolders.find((folder) => folder.id === wrikeFolderMeeting.wrikeFolderId)
      : null;
  }, [wrikeFolderMeetings, wrikeRemoteFolders, meetingId]);

  // Set the default value when the dialog opens or when the association changes
  useEffect(() => {
    setSelectedFolderId(wrikeFolderMeetingAssociation?.id || '');
  }, [wrikeFolderMeetingAssociation]);

  // Close the dialog after successful API response
  useEffect(() => {
    if (hasSubmitted && !showWrikeSyncLoader) {
      dispatch(closeDialog('wrikeFoldersDialog'));
    }
  }, [hasSubmitted, showWrikeSyncLoader, dispatch]);

  const onClose = useCallback(() => {
    dispatch(closeDialog('wrikeFoldersDialog'));
  }, [dispatch]);

  const onSubmit = useCallback(() => {
    setHasSubmitted(true);
    dispatch(syncWrikeFolders({ wrikeFolderId: selectedFolderId || null, meetingId }));
  }, [dispatch, selectedFolderId, meetingId]);

  const options = useMemo(
    () =>
      wrikeRemoteFolders
        .filter(
          (folder) =>
            !wrikeFolderMeetings.some((wfm) => wfm.wrikeFolderId === folder.id && wfm.meetingId !== String(meetingId)),
        )
        .map((folder) => ({
          id: folder.id,
          label: folder.title,
        })),
    [wrikeRemoteFolders, wrikeFolderMeetings, meetingId],
  );

  return (
    <ConnectedDialog dialogKey="wrikeFoldersDialog">
      <div className="popup" style={{ display: 'block', width: 700 }}>
        <div className="panel">
          <div className="panel-header small-header flex jcsb">
            <h3>Manage Wrike Folder & Meeting Pair</h3>
            <span className="close-popup close" onClick={onClose}>
              <i className="icon-close"></i>
            </span>
          </div>
          <div className="panel-body">
            {showWrikeSyncLoader ? (
              <div className="flex aic jcc" style={{ padding: '20px' }}>
                <Loader type="TailSpin" color="var(--theme-primary-color)" height={20} width={20} />
                <span className="ml-2">Syncing folders...</span>
              </div>
            ) : (
              <div className="row">
                <div className="col-12">
                  <div className="field-container">
                    <label htmlFor="wrikeFolder">Wrike Folder</label>
                    <SelectField
                      id="wrikeFolder"
                      value={selectedFolderId}
                      onChange={setSelectedFolderId}
                      options={options}
                      placeholder="Select a Wrike folder"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="panel-footer no-border flex jcr">
            <a className="link blueGray mr-2 close" title="" onClick={onClose}>
              Close
            </a>
            <button
              type="button"
              className="btn btn-green"
              title=""
              onClick={onSubmit}
              disabled={!!showWrikeSyncLoader}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </ConnectedDialog>
  );
};
